import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { formatResetTime } from 'features/usage/utils/usage-format';
import { usePlanRequestStatus, useRequestPlanChange } from '../hooks/use-credits';
import { useUsageLimitReached } from '../hooks/use-usage-limit-reached';
import { UsageRequestDialog } from './usage-request-dialog';

/** Above the chat box once the plan limit is used up and no top-up credits cover it. Only a member can request credit. */
export const UsageLimitBanner = () => {
  const { t } = useTranslation();
  const limit = useUsageLimitReached();
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const planRequest = useRequestPlanChange();
  const requestStatus = usePlanRequestStatus(isRequestOpen);

  if (!limit) return null;

  const resetTime = limit.resetsAt ? formatResetTime(limit.resetsAt, t) : '';

  return (
    <>
      <div
        role="status"
        className="flex flex-col gap-2 rounded-xl border border-border bg-background px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
      >
        <div className="flex min-w-0 items-start gap-2">
          <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-error" />
          <div className="min-w-0 space-y-0.5">
            <p className="text-sm text-foreground">
              <span className="font-medium text-error">
                {t('USAGE_LIMIT_REACHED', 'Usage limit reached')}
              </span>
              {resetTime && (
                <>
                  {' · '}
                  {t('WINDOW_RESETS', { when: resetTime, defaultValue: 'Resets {{when}}' })}
                </>
              )}
              {' · '}
              <Link to="/usage" className="font-medium underline">
                {t('LIMITS_SHARED_WITH_KLAX', 'limits shared with Klax!')}
              </Link>
            </p>
            <p className="text-xs text-muted-foreground">
              {t(
                'USAGE_LIMIT_BANNER_HINT',
                'Wait for the reset, or keep working now with usage credits, billed on top of your plan.'
              )}
            </p>
          </div>
        </div>
        {!isAdmin && (
          <Button
            variant="ghost"
            className="h-7 shrink-0 self-start !rounded-field bg-soft-button px-2 text-sm font-normal text-soft-button-foreground hover:bg-grey-100 hover:text-soft-button-foreground dark:hover:bg-grey-800 sm:self-auto"
            onClick={() => setIsRequestOpen(true)}
          >
            {t('CREDIT_REQUEST', 'Credit request')}
          </Button>
        )}
      </div>
      <UsageRequestDialog
        open={isRequestOpen}
        onClose={() => setIsRequestOpen(false)}
        isSending={planRequest.isPending}
        isLoading={requestStatus.isFetching}
        isPending={!!requestStatus.data?.pending}
        onSend={() =>
          planRequest.mutate(limit.limitName, { onSuccess: () => setIsRequestOpen(false) })
        }
      />
    </>
  );
};
